/**
 * Ordering for the Workspace Selector's "other workspaces" list.
 *
 * IMPORTANT — this sorts ONLY the rows currently loaded in the browser.
 * `POST /v0/workspace/list` paginates server-side ordered by `created_at asc`
 * and has no `order_by` support, so with more than one page the list is
 * "page 1 sorted", not "all your workspaces sorted": loading the next page via
 * "Show N more" appends rows that then re-sort into the middle of what the user
 * was already looking at. Accepted for V1 (the role/recency data it sorts by is
 * mock anyway — see `__mocks__/workspaceSelector.mock.js`); true full-dataset
 * sorting is a backend dependency (`order_by` on that endpoint) tracked on
 * CT-4939. Do not read this as sorting the whole set.
 *
 * The active (hero) workspace is not part of this — it stays pinned above the
 * list in every mode, so callers sort only the non-active rows.
 */

/**
 * Seniority order for the `role` mode. ARP asks for Platform Admin first, so
 * this cannot be alphabetical: `localeCompare` on the labels would yield
 * Member → Org Admin → Platform Admin → Viewer.
 *
 * Keys are the mock role labels from `synthesizeWorkspaceMeta`. An unknown
 * role sorts last rather than silently landing first.
 */
const ROLE_RANK = {
  'Platform Admin': 0,
  'Org Admin': 1,
  Member: 2,
  Viewer: 3,
};

const UNKNOWN_ROLE_RANK = Object.keys(ROLE_RANK).length;

/** Case- and accent-insensitive name comparison, used on its own and as tiebreaker. */
function byName(a, b) {
  return (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' });
}

const COMPARATORS = {
  /** Most recently visited first — mock `recencyRank`, where 0 is most recent. */
  recent: (metaById) => (a, b) => {
    const rank =
      (metaById[a.id]?.recencyRank ?? Number.MAX_SAFE_INTEGER) -
      (metaById[b.id]?.recencyRank ?? Number.MAX_SAFE_INTEGER);
    return rank || byName(a, b);
  },
  /** Alphabetical by workspace name. */
  alpha: () => byName,
  /** Platform Admin → Org Admin → Member → Viewer, then name. */
  role: (metaById) => (a, b) => {
    const rank =
      (ROLE_RANK[metaById[a.id]?.role] ?? UNKNOWN_ROLE_RANK) -
      (ROLE_RANK[metaById[b.id]?.role] ?? UNKNOWN_ROLE_RANK);
    return rank || byName(a, b);
  },
};

/**
 * Sort workspaces for the given mode. Pure — returns a new array.
 *
 * @param {Array<{id: string, name: string}>} workspaces
 * @param {'recent'|'alpha'|'role'} mode
 * @param {Record<string, {role?: string, recencyRank?: number}>} metaById
 *   Display metadata by workspace id (`recencyRank` and `role` live there, not
 *   on the workspace itself).
 * @returns {Array<object>}
 */
export function sortWorkspaces(workspaces, mode, metaById = {}) {
  const build = COMPARATORS[mode] ?? COMPARATORS.recent;
  return [...workspaces].sort(build(metaById));
}

export { ROLE_RANK };
export default sortWorkspaces;
